import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const authHeader = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` });

function mapAttemptStatus(s) {
    const m = {
        created: 'Utworzony',
        pending_verification: 'Weryfikacja',
        in_progress: 'W trakcie',
        submitted: 'Zgłoszony',
        awaiting_manual: 'Oczekuje na ocenę',
        auto_graded: 'Oceniony automatycznie',
        completed: 'Zakończony',
        canceled: 'Unieważniony',
        no_attempt: 'Nie rozpoczęto',
    };
    return m[s] || s || '—';
}
async function exportXlsx(id) {
    try {
        const r = await fetch(`/api/prowadzacy/termin/${id}/export-xlsx`, {
            method: 'GET',
            credentials: 'include',
            headers: { ...authHeader() },
        });
        if (!r.ok) {
            const j = await r.json().catch(() => ({}));
            throw new Error(j?.blad || `Błąd eksportu (${r.status})`);
        }
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `wyniki-termin-${id}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    } catch (e) {
        alert(e.message || 'Nie udało się pobrać pliku');
    }
}

function UserIcon({ color = '#9ca3af', title }) {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" aria-label={title || 'Weryfikacja'} role="img"
             style={{ display:'inline-block', verticalAlign:'middle', fill: color }}>
            <path d="M12 12c2.761 0 5-2.239 5-5s-2.239-5-5-5-5 2.239-5 5 2.239 5 5 5zm0 2c-4.418 0-8 2.239-8 5v1h16v-1c0-2.761-3.582-5-8-5z"/>
        </svg>
    );
}
function verificationColor(status) {
    const s = (status || '').toLowerCase();
    if (s === 'auto_pass' || s === 'manual_approved') return '#16a34a';
    if (s === 'manual_pending') return '#d97706';
    if (s === 'manual_rejected') return '#dc2626';
    return '#9ca3af';
}
function VerificationCell({ row, navigate }) {
    const s = (row.verification_status || '').toLowerCase();
    const color = verificationColor(s);
    return (
        <div style={{ display:'flex', alignItems:'center', gap:8, textAlign:'center' }}>
            <UserIcon color={color} title={s || 'not_required'} />
        </div>
    );
}
function ProctoringCell({ value }) {
    const n = typeof value === 'number' ? value : (value == null ? null : Number(value));
    const v = Number.isFinite(n) ? n : null;
    let dot = '#9ca3af';
    if (v != null) {
        if (v < 20) dot = '#16a34a';
        else if (v <= 100) dot = '#d97706';
        else dot = '#dc2626';
    }
    return (
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span aria-hidden="true" style={{ fontSize:18, lineHeight:1, color: dot }}>●</span>
            <span style={{ color:'#374151' }}>( {v != null ? v : '—'} )</span>
        </div>
    );
}
function SEB({ value }) {
    let dot = '#9ca3af';
    if (value != null) {
        if (value === 'seb') dot = '#16a34a';
        else dot = '#dc2626';
    }
    return (
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span aria-hidden="true" style={{ fontSize:18, lineHeight:1, color: dot }}>●</span>
        </div>
    );
}
function ActionsCell({ row, navigate }) {
    const goDetails = () => { if (row.attempt_id) navigate(`/prowadzacy/attempt/${row.attempt_id}`); };
    return (
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <button  className={'but-smal'} style={{padding:'5px 10px', boxShadow:'none'}} onClick={goDetails} disabled={!row.attempt_id}>Szczegóły</button>
        </div>
    );
}
function formatPoints(row) {
    if (!row.attempt_id) return '—';
    const pts = row.attempt_points ? row.attempt_points : null;
    const max = row.attempt_max_points ? row.attempt_max_points : null;
    if (pts == null && max == null) return '—';
    if (pts != null && max != null) {
        const pct = max > 0 ? Math.round((pts / max) * 100) : 0;
        return `${pts} / ${max} (${pct}%)`;
    }
    if (pts != null) return String(pts);
    return `— / ${max}`;
}

function Table({ title, rows, navigate }) {
    return (
        <div style={{ border:'1px solid #e5e7eb', borderRadius:12, padding:12, background:'#fff' }}>
            <div style={{ fontWeight:700, marginBottom:8 }}>{title}</div>
            <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'separate', borderSpacing:0 }}>
                    <thead>
                    <tr style={{ background:'#fafafa' }}>
                        <th style={th}>Imię</th>
                        <th style={th}>Nazwisko</th>
                        <th style={th}>Album</th>
                        <th style={th}>Status</th>
                        <th style={th}>Punkty</th>
                        <th style={th}>Weryfikacja</th>
                        <th style={th}>Proctoring</th>
                        <th style={th}>SEB</th>
                        <th style={th}></th>
                    </tr>
                    </thead>
                    <tbody>
                    {rows.length === 0 ? (
                        <tr><td colSpan={9} style={{ padding:12, textAlign:'center', color:'#6b7280' }}>Brak danych</td></tr>
                    ) : rows.map((r, i) => {
                        const status = r.attempt_id ? (r.status || '—') : 'no_attempt';
                        return (
                            <tr key={`${r.user_id}-${i}`} style={{ borderBottom:'1px solid #eee' }}>
                                <td style={td}>{r.first_name || '—'}</td>
                                <td style={td}>{r.last_name || '—'}</td>
                                <td style={td}><code>{r.indeks || '—'}</code></td>
                                <td style={td}>{mapAttemptStatus(status)}</td>
                                <td style={td}>{formatPoints(r)}</td>
                                <td style={td}><VerificationCell row={r} navigate={navigate} /></td>
                                <td style={td}><ProctoringCell value={r.attempt_flags} /></td>
                                <td style={td}><SEB value={r.active_client} /></td>
                                <td style={{ ...td, textAlign:'right' }}><ActionsCell row={r} navigate={navigate} /></td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
const th = { textAlign:'left', padding:'10px 8px', fontSize:13, color:'#374151', borderBottom:'1px solid #eee' };
const td = { padding:'10px 8px', fontSize:14, verticalAlign:'middle' };

export default function ExamTermManager(){
    const { id } = useParams(); // term_id
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [term, setTerm] = useState(null);
    const [members, setMembers] = useState([]);
    const [guests, setGuests] = useState([]);

    // Extra time
    const [addMin, setAddMin] = useState('');
    const [adding, setAdding] = useState(false);
    const [okMsg, setOkMsg] = useState('');

    // Term note
    const [termNote, setTermNote] = useState('');
    const [savingNote, setSavingNote] = useState(false);
    const [noteMsg, setNoteMsg] = useState('');
    const [approveOpen, setApproveOpen] = useState(false);
    const [approveMode, setApproveMode] = useState('below_threshold');
    const [approveThreshold, setApproveThreshold] = useState(200);
    const [approving, setApproving] = useState(false);
    const [approveMsg, setApproveMsg] = useState('');

    // --- NOWOŚĆ: import pytań
    const fileInputRef = useRef(null);
    const [importing, setImporting] = useState(false);
    const [importError, setImportError] = useState('');
    const [importOk, setImportOk] = useState('');
    // --- NOWOŚĆ: obsługa importu
    const handleClickImport = () => {
        fileInputRef.current?.click();
    };
    async function readApiError(res, fallbackMsg = `Błąd (HTTP ${res.status})`) {
        try {
            const data = await res.json();
            if (typeof data === 'string') return data;
            if (Array.isArray(data?.errors) && data.errors.length) return data.errors.join(', ');
            // ⬇️ preferuj 'blad', potem inne popularne klucze
            return data?.blad || data?.message || data?.error || fallbackMsg;
        } catch {
            try {
                const t = await res.text();
                return t || fallbackMsg;
            } catch {
                return fallbackMsg;
            }
        }
    }
    const handleImportFile = async (e) => {
        const file = e.target.files?.[0];
        // pozwól na ponowny wybór tego samego pliku później
        e.target.value = '';

        if (!file) return;
        setImportError('');
        setImportOk('');
        setImporting(true);

        try {
            const form = new FormData();
            form.append('file', file);
            // jeżeli backend wymaga powiązania z egzaminem:

            const res = await fetch('/api/exam/import', {
                method: 'POST',
                headers: {
                    // nie ustawiaj Content-Type — przeglądarka doda boundary
                    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
                },
                body: form
            });

            if (!res.ok) {
                const msg = await readApiError(res, `Import nieudany (HTTP ${res.status})`);
                throw new Error(msg); // <-- pokaże dokładnie wartość z 'blad', jeśli backend ją zwraca
            }

            // opcjonalnie pokaż ile wczytano
            const payload = await res.json().catch(() => null);
            const importedCount =
                payload?.imported ?? payload?.count ?? payload?.created ?? null;

            setImportOk(importedCount != null
                ? `Zaimportowano ${importedCount} pyt.`
                : 'Import zakończony powodzeniem.');

            // odśwież strukturę egzaminu po imporcie
        } catch (err) {
            setImportError(err?.message || 'Wystąpił błąd podczas importu.');
        } finally {
            setImporting(false);
        }
    };


    useEffect(() => {
        let timer;
        let cancelled = false;

        async function load() {
            try {
                setErr('');
                const r = await fetch(`/api/prowadzacy/termin/${id}/przebieg`, {
                    credentials: 'include',
                    headers: { ...authHeader() }
                });
                if (!r.ok) {
                    const j = await r.json().catch(()=> ({}));
                    throw new Error(j?.blad || `Błąd pobierania (${r.status})`);
                }
                const j = await r.json();
                if (cancelled) return;
                setTerm(j.term || null);
                setMembers(j.members || []);
                setGuests(j.guests || []);
                setTermNote((j.term && j.term.note) ? j.term.note : '');
            } catch (e) {
                if (!cancelled) setErr(e.message || 'Błąd pobierania');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        timer = setInterval(load, 30000);

        return () => { cancelled = true; clearInterval(timer); };
    }, [id]);

    const startAtStr = useMemo(() => {
        if (!term?.exam_datetime) return '—';
        try {
            return new Intl.DateTimeFormat('pl-PL', { dateStyle:'medium', timeStyle:'short' })
                .format(new Date(term.exam_datetime));
        } catch { return term.exam_datetime; }
    }, [term?.exam_datetime]);

    async function addExtraTimeForTerm() {
        setOkMsg('');
        const m = Number(addMin);
        if (!Number.isFinite(m) || m <= 0) {
            alert('Podaj dodatnią liczbę minut.');
            return;
        }
        setAdding(true);
        try {
            const r = await fetch(`/api/exam/termin/${id}/extra-time`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({ minutes: m })
            });
            if (!r.ok) {
                const j = await r.json().catch(() => ({}));
                throw new Error(j?.blad || `Błąd dodawania czasu (${r.status})`);
            }
            const j = await r.json();
            setOkMsg(`Dodano ${j.added_minutes} min do ${j.affected_attempts} podejść.`);
            // odśwież dane
            try {
                const rr = await fetch(`/api/prowadzacy/termin/${id}/przebieg`, {
                    credentials: 'include',
                    headers: { ...authHeader() }
                });
                if (rr.ok) {
                    const jj = await rr.json();
                    setTerm(jj.term || null);
                    setMembers(jj.members || []);
                    setGuests(jj.guests || []);
                    setTermNote((jj.term && jj.term.note) ? jj.term.note : '');
                }
            } catch {}
        } catch (e) {
            alert(e.message || 'Błąd dodawania czasu');
        } finally {
            setAdding(false);
        }
    }

    async function saveTermNote(){
        setSavingNote(true); setNoteMsg('');
        try{
            const r = await fetch(`/api/prowadzacy/termin/${id}/note`, {
                method:'PATCH',
                credentials:'include',
                headers: { 'Content-Type':'application/json', ...authHeader() },
                body: JSON.stringify({ note: termNote })
            });
            if (!r.ok){
                const j = await r.json().catch(()=> ({}));
                throw new Error(j?.blad || `Błąd zapisu notatki (${r.status})`);
            }
            setNoteMsg('Zapisano notatkę.');
        }catch(e){
            setNoteMsg(e.message || 'Błąd zapisu notatki');
        }finally{
            setSavingNote(false);
        }
    }

    function openApproveModal() {
        setApproveMsg('');
        setApproveMode('below_threshold');
        setApproveThreshold(200);
        setApproveOpen(true);
    }

    async function refreshAfterApproval() {
        const r = await fetch(`/api/prowadzacy/termin/${id}/przebieg`, {
            credentials: 'include',
            headers: { ...authHeader() }
        });
        if (!r.ok) return;
        const j = await r.json();
        setTerm(j.term || null);
        setMembers(j.members || []);
        setGuests(j.guests || []);
        setTermNote((j.term && j.term.note) ? j.term.note : '');
    }

    async function approveResults() {
        setApproveMsg('');
        const threshold = Number(approveThreshold);
        if (approveMode === 'below_threshold' && (!Number.isFinite(threshold) || threshold < 0)) {
            setApproveMsg('Podaj prawidłową liczbę punktów karnych.');
            return;
        }

        setApproving(true);
        try {
            const r = await fetch(`/api/prowadzacy/termin/${id}/complete-attempts`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json', ...authHeader() },
                body: JSON.stringify({ mode: approveMode, threshold })
            });
            const j = await r.json().catch(() => ({}));
            if (!r.ok) throw new Error(j?.blad || `Błąd zatwierdzania (${r.status})`);

            const skipped = Number(j?.skipped_by_penalty || 0);
            setApproveMsg(`Zatwierdzono ${j?.approved || 0} wyników.${skipped ? ` Pominięto ${skipped} podejść z punktami karnymi na poziomie progu lub powyżej.` : ''}`);
            await refreshAfterApproval();
        } catch (e) {
            setApproveMsg(e.message || 'Błąd zatwierdzania wyników.');
        } finally {
            setApproving(false);
        }
    }

    return (
        <div style={{ maxWidth: 1100, padding:'0 16px', display:'grid', gap:16, marginTop: 80 }}>
            <h1>Przebieg terminu</h1>

            {term && (
                <>
                    <div style={{ border:'1px solid #e5e7eb', borderRadius:12, padding:12, background:'#fff' }}>
                        <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                            <div><b>Termin:</b> <code>#{term.term_id}</code></div>
                            <div><b>Start:</b> {startAtStr}</div>
                            <div><b>Czas:</b> {term.exam_duration} min</div>
                            <div><b>Proctoring:</b> {term.proctoring ? 'tak' : 'nie'}</div>
                            <button
                                className="but-smal"
                                onClick={()=>navigate(`/prowadzacy/termin/${term.term_id}`)}
                                style={{ boxShadow:'none', padding:'8px 12px' }}
                            >
                                Szczegóły
                            </button>
                        </div>
                    </div>
                    <div style={{ textAlign:'right' , paddingTop:8 }}>
                        <button
                            className={'but-smal'}
                            onClick={() => exportXlsx(term.term_id)}
                            style={{ width:'300px' }}
                        >
                            Eksport do Excel (XLSX)
                        </button>
                        <button
                            className={'but-smal'}
                            onClick={openApproveModal}
                            style={{ marginLeft:'10px', width:'220px' }}
                        >
                            Zatwierdź wyniki
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls,.csv,.json,.zip,.txt"
                            onChange={handleImportFile}
                            style={{ display: 'none' }}
                        />
                        <button onClick={handleClickImport}  style={{ marginLeft:'10px' }} className={'but-smal'} disabled={importing} title="Importuj pytania z pliku">
                            {importing ? 'Importowanie…' : ' Importuj ocenianie'}
                        </button>

                        {(importError || importOk) && (
                            <div style={{ marginTop: '0.5rem' }}>
                                {importError && <div style={{ color: 'crimson' }}>{importError}</div>}
                                {importOk && <div style={{ color: 'green' }}>{importOk}</div>}
                            </div>
                        )}

                    </div>
                </>

            )}

            {loading ? (
                <div>Ładowanie…</div>
            ) : err ? (
                <div style={{ color:'#b91c1c' }}>{err}</div>
            ) : (
                <>
                    <Table title="Studenci z grupy" rows={members} navigate={navigate} />
                    <Table title="Goście (kod egzaminu)" rows={guests} navigate={navigate} />
                </>
            )}

            {term && (
                <div style={{ border:'1px solid #e5e7eb', borderRadius:12, padding:12, background:'#fff' }}>
                    <div style={{ fontWeight:700, marginBottom:8 }}>Notatka dla wszystkich (wyświetlana studentom)</div>
                    <textarea
                        rows={3}
                        value={termNote}
                        onChange={e=>setTermNote(e.target.value)}
                        style={{ width:'100%', border:'1px solid #e5e7eb', borderRadius:8, padding:8 }}
                    />
                    <div style={{ marginTop:8, display:'flex', gap:8, alignItems:'center' }}>
                        <button className={'but-smal'} onClick={saveTermNote} disabled={savingNote}>
                            {savingNote ? 'Zapisywanie…' : 'Zapisz notatkę'}
                        </button>
                        {noteMsg && <span style={{ color: noteMsg.includes('Błąd') ? '#dc2626' : '#059669' }}>{noteMsg}</span>}
                    </div>
                </div>
            )}

            {term && (
                <div style={{ border:'1px solid #e5e7eb', borderRadius:12, padding:12, background:'#fff' }}>
                    <div style={{ fontWeight:700, marginBottom:8 }}>Dodaj czas</div>
                    <div style={{ color:'#6b7280', marginBottom:8 }}>
                        Przyznaj wszystkim <b>aktywnym</b> uczestnikom tego terminu dodatkowy czas.
                    </div>
                    <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                        <label>
                            <span style={{ marginRight:6 }}>Liczba minut:</span>
                            <input
                                type="number"
                                min="1"
                                step="1"
                                value={addMin}
                                onChange={e => setAddMin(e.target.value)}
                                style={{ width:120 }}
                            />
                        </label>
                        <button
                            className={'but-smal'}
                            onClick={addExtraTimeForTerm}
                            disabled={adding}
                            style={{  padding:'8px 12px', boxShadow:'none'}}
                        >
                            {adding ? 'Dodaję…' : 'Dodaj czas wszystkim'}
                        </button>
                        {okMsg && <span style={{ color:'#059669' }}>{okMsg}</span>}
                    </div>
                </div>
            )}

            {approveOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position:'fixed',
                        inset:0,
                        background:'rgba(0,0,0,.35)',
                        display:'grid',
                        placeItems:'center',
                        padding:16,
                        zIndex:60
                    }}
                >
                    <div style={{ position:'relative', background:'#fff', borderRadius:12, padding:18, width:'min(620px, 95vw)', boxShadow:'0 10px 30px rgba(0,0,0,.18)' }}>
                        <button
                            type="button"
                            aria-label="Zamknij"
                            onClick={() => setApproveOpen(false)}
                            disabled={approving}
                            style={{
                                position:'absolute',
                                top:10,
                                right:12,
                                border:'none',
                                background:'transparent',
                                color:'#0f2a5f',
                                fontSize:24,
                                fontWeight:700,
                                lineHeight:1,
                                cursor: approving ? 'not-allowed' : 'pointer',
                                padding:0
                            }}
                        >
                            ×
                        </button>
                        <h3 style={{ marginTop:0 }}>Zatwierdź wyniki</h3>
                        <p style={{ color:'#374151' }}>
                            Wyniki zostaną zatwierdzone i będą traktowane jako zakończone.
                        </p>

                        <div style={{ display:'grid', gap:10, marginTop:14 }}>
                            <label style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                                <input
                                    type="radio"
                                    name="approveMode"
                                    checked={approveMode === 'all'}
                                    onChange={() => setApproveMode('all')}
                                />
                                <span>Zatwierdź wszystko.</span>
                            </label>

                            <label style={{ display:'flex', gap:8, alignItems:'flex-start' }}>
                                <input
                                    type="radio"
                                    name="approveMode"
                                    checked={approveMode === 'below_threshold'}
                                    onChange={() => setApproveMode('below_threshold')}
                                />
                                <span>Zatwierdź egzaminy poniżej określonego poziomu zaufania.</span>
                            </label>

                            <label style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginLeft:24 }}>
                                <span>Zaakceptuj poniżej</span>
                                <input
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={approveThreshold}
                                    disabled={approveMode !== 'below_threshold'}
                                    onChange={e => setApproveThreshold(e.target.value)}
                                    style={{ width:110, padding:'8px', border:'1px solid #d1d5db', borderRadius:8 }}
                                />
                                <span>punktów karnych. Pozostałe należy sprawdzić i zaakceptować ręcznie.</span>
                            </label>
                        </div>

                        <p style={{ marginTop:14, color:'#6b7280', lineHeight:1.45 }}>
                            Punkty karne to punkty za naruszenia podczas egzaminu. Powyżej 200 istnieje duże
                            prawdopodobieństwo, że ktoś oszukiwał, np. korzystał z przeglądarki do sprawdzenia wyniku.
                        </p>

                        {approveMsg && (
                            <div style={{ marginTop:10, color: approveMsg.includes('Błąd') || approveMsg.includes('prawidłową') ? '#dc2626' : '#059669' }}>
                                {approveMsg}
                            </div>
                        )}

                        <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:16 }}>
                            <button
                                type="button"
                                onClick={() => setApproveOpen(false)}
                                disabled={approving}
                                style={{
                                    border:'1px solid #0f2a5f',
                                    background:'#f3f4f6',
                                    color:'#0f2a5f',
                                    borderRadius:8,
                                    padding:'8px 14px',
                                    fontWeight:600,
                                    cursor: approving ? 'not-allowed' : 'pointer'
                                }}
                            >
                                Powrót
                            </button>
                            <button
                                className={'but-smal'}
                                onClick={approveResults}
                                disabled={approving}
                            >
                                {approving ? 'Zatwierdzanie…' : 'Zatwierdź'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
